import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { initials } from "@/lib/format";

/**
 * Textarea with @-mention autocomplete. Calls `onChange(body, mentionedUserIds)`.
 * Mentions are stored as plain `@FullName` tokens in the body, but we keep the
 * matched user_id set separately so the backend can create precise notifications.
 */
const MentionTextarea = forwardRef(function MentionTextarea(
  { value, onChange, users, placeholder, className, dataTestId },
  ref
) {
  const taRef = useRef(null);
  const [showPicker, setShowPicker] = useState(false);
  const [query, setQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(null); // index in value where '@' was typed
  const [highlight, setHighlight] = useState(0);
  const [mentionedIds, setMentionedIds] = useState([]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setMentionedIds([]);
      setShowPicker(false);
      setMentionStart(null);
    },
  }));

  const filtered = users
    .filter((u) => {
      const s = query.toLowerCase();
      return !s || u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
    })
    .slice(0, 6);

  const detectMention = (text, caret) => {
    // Walk back from caret looking for the most recent '@'
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === "@") {
        // Ensure '@' is at start or preceded by whitespace
        if (i === 0 || /\s/.test(text[i - 1])) {
          const q = text.slice(i + 1, caret);
          if (/^[\w.\-]*$/.test(q)) return { start: i, query: q };
        }
        return null;
      }
      if (/\s/.test(ch)) return null;
      i--;
    }
    return null;
  };

  const onTextChange = (e) => {
    const next = e.target.value;
    const caret = e.target.selectionStart;
    const m = detectMention(next, caret);
    if (m) {
      setMentionStart(m.start);
      setQuery(m.query);
      setShowPicker(true);
      setHighlight(0);
    } else {
      setShowPicker(false);
      setMentionStart(null);
    }
    // Re-derive mentionedIds from current text — keep only those still appearing
    const stillIn = mentionedIds.filter((id) => {
      const u = users.find((x) => x.id === id);
      return u && next.includes(`@${u.name}`);
    });
    setMentionedIds(stillIn);
    onChange(next, stillIn);
  };

  const insertMention = (user) => {
    if (mentionStart == null) return;
    const before = value.slice(0, mentionStart);
    const caret = taRef.current?.selectionStart ?? value.length;
    const after = value.slice(caret);
    const token = `@${user.name} `;
    const next = before + token + after;
    const nextIds = mentionedIds.includes(user.id) ? mentionedIds : [...mentionedIds, user.id];
    setMentionedIds(nextIds);
    setShowPicker(false);
    setMentionStart(null);
    onChange(next, nextIds);
    // restore caret position
    setTimeout(() => {
      const pos = (before + token).length;
      taRef.current?.setSelectionRange(pos, pos);
      taRef.current?.focus();
    }, 0);
  };

  const onKeyDown = (e) => {
    if (showPicker) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filtered[highlight]) { e.preventDefault(); insertMention(filtered[highlight]); return; }
      }
      if (e.key === "Escape") { e.preventDefault(); setShowPicker(false); return; }
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={taRef}
        value={value}
        onChange={onTextChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        data-testid={dataTestId}
      />
      {showPicker && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 left-0 right-0 sm:right-auto sm:w-64 bg-white border border-neutral-200 rounded-sm shadow-lg p-1" data-testid="mention-picker">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-2 py-1">Mention someone</div>
          {filtered.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-left ${i === highlight ? "bg-blue-50" : "hover:bg-neutral-100"}`}
              data-testid={`mention-option-${u.id}`}
            >
              <div className="w-5 h-5 rounded-full bg-neutral-950 text-white text-[9px] font-bold flex items-center justify-center">{initials(u.name)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{u.name}</div>
                <div className="text-[10px] text-neutral-500 truncate">{u.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default MentionTextarea;

// Helper to render @mentions as styled spans inside a comment body.
export function renderBodyWithMentions(body, users) {
  if (!body) return null;
  const names = users.map((u) => u.name).sort((a, b) => b.length - a.length);
  if (names.length === 0) return body;
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const re = new RegExp(`@(${escaped})`, "g");
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(body.slice(last, m.index));
    out.push(
      <span key={`${m.index}-${m[1]}`} className="bg-blue-100 text-blue-800 font-semibold px-1 rounded-sm">
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}
